import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "reactstrap";
import { toast } from "react-toastify";
import { getAeConversionReport } from "../../../../Api/apiUtils";
import {
  Page,
  PageHeader,
  Card,
  Button,
  Field,
  TextInput,
  Toolbar,
  Table,
  Loader,
  EmptyState,
} from "../../../../ui";
import "./aeReports.scss";

export type ConvertedStudentDetail = {
  enrollmentDate?: string;
  studentId?: string;
  studentName?: string;
  mobilenumber?: string;
  emailId?: string;
};

type RegionRaw = {
  classConducted?: number;
  classesNotConducted?: number;
  classesNotMarked?: number;
  convertedStudents?: number;
  convertedStudentsDetails?: ConvertedStudentDetail[];
};

export type AeConversionRawRow = {
  TeacherId?: string | number;
  TeacherName?: string;
  CategoryName?: string;
  TotalSlots?: number;
  Domestic?: RegionRaw;
  International?: RegionRaw;
};

export type RegionNorm = {
  classConducted: number;
  classesNotConducted: number;
  classesNotMarked: number;
  convertedStudents: number;
  convertedStudentsDetails: ConvertedStudentDetail[];
  regionTotal: number;
  conductedPctStr: string;
};

export type AeConversionEnrichedRow = AeConversionRawRow & {
  domestic: RegionNorm;
  international: RegionNorm;
  totalConducted: number;
  totalNotConducted: number;
  totalNotMarked: number;
  totalConverted: number;
  conversionPctStr: string;
};

const toNum = (v: unknown): number => {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
};

function normalizeRegion(raw?: RegionRaw): RegionNorm {
  const classConducted = toNum(raw?.classConducted);
  const classesNotConducted = toNum(raw?.classesNotConducted);
  const classesNotMarked = toNum(raw?.classesNotMarked);
  const convertedStudents = toNum(raw?.convertedStudents);
  const convertedStudentsDetails = Array.isArray(raw?.convertedStudentsDetails)
    ? raw!.convertedStudentsDetails!
    : [];
  const regionTotal = classConducted + classesNotConducted + classesNotMarked;
  const conductedPctStr =
    regionTotal > 0 ? ((classConducted / regionTotal) * 100).toFixed(2) : "0";
  return {
    classConducted,
    classesNotConducted,
    classesNotMarked,
    convertedStudents,
    convertedStudentsDetails,
    regionTotal,
    conductedPctStr,
  };
}

/** Supports top-level array or `{ data: [...] }` wrapper from the API. */
function readConversionRows(payload: unknown): AeConversionRawRow[] {
  if (Array.isArray(payload)) return payload as AeConversionRawRow[];
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as { data?: unknown }).data)
  ) {
    return (payload as { data: AeConversionRawRow[] }).data;
  }
  return [];
}

export function enrichAeConversionRows(raw: unknown): AeConversionEnrichedRow[] {
  const list = readConversionRows(raw);
  return list.map((row) => {
    const domestic = normalizeRegion(row.Domestic);
    const international = normalizeRegion(row.International);
    const totalConducted = domestic.classConducted + international.classConducted;
    const totalNotConducted =
      domestic.classesNotConducted + international.classesNotConducted;
    const totalNotMarked = domestic.classesNotMarked + international.classesNotMarked;
    const totalConverted = domestic.convertedStudents + international.convertedStudents;
    const denom = totalConducted || 1;
    const conversionPctStr = ((totalConverted / denom) * 100).toFixed(2);
    return {
      ...row,
      domestic,
      international,
      totalConducted,
      totalNotConducted,
      totalNotMarked,
      totalConverted,
      conversionPctStr,
    };
  });
}

function formatEnrollmentDate(raw?: string): string {
  if (!raw) return "—";
  const d = new Date(raw.includes("T") ? raw : `${raw}T12:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function rowHasConversionDetails(row: AeConversionEnrichedRow): boolean {
  return (
    row.domestic.convertedStudentsDetails.length > 0 ||
    row.international.convertedStudentsDetails.length > 0
  );
}

function conductedPctFromTotals(conducted: number, notConducted: number, notMarked: number): string {
  const total = conducted + notConducted + notMarked;
  return total > 0 ? ((conducted / total) * 100).toFixed(2) : "0";
}

function exportConversionCsv(rows: AeConversionEnrichedRow[]): void {
  const headers = [
    "Teacher ID",
    "Teacher Name",
    "Category",
    "Total Slots",
    "Classes Conducted Domestic",
    "Classes Conducted International",
    "Classes Conducted Total",
    "Classes Not Conducted Domestic",
    "Classes Not Conducted International",
    "Classes Not Conducted Total",
    "Classes Not Marked Domestic",
    "Classes Not Marked International",
    "Classes Not Marked Total",
    "Classes Conducted % Domestic",
    "Classes Conducted % International",
    "Classes Conducted % Total",
    "Converted Students Domestic",
    "Converted Students International",
    "Converted Students Total",
    "Conversion %",
  ];
  const esc = (v: string | number) => {
    const s = `${v}`.replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = rows.map((r) => {
    const totalConductedPct = conductedPctFromTotals(
      r.totalConducted,
      r.totalNotConducted,
      r.totalNotMarked
    );
    return [
      r.TeacherId ?? "",
      r.TeacherName ?? "",
      r.CategoryName ?? "",
      toNum(r.TotalSlots),
      r.domestic.classConducted,
      r.international.classConducted,
      r.totalConducted,
      r.domestic.classesNotConducted,
      r.international.classesNotConducted,
      r.totalNotConducted,
      r.domestic.classesNotMarked,
      r.international.classesNotMarked,
      r.totalNotMarked,
      r.domestic.conductedPctStr,
      r.international.conductedPctStr,
      totalConductedPct,
      r.domestic.convertedStudents,
      r.international.convertedStudents,
      r.totalConverted,
      r.conversionPctStr,
    ]
      .map(esc)
      .join(",");
  });
  const blob = new Blob([[headers.map(esc).join(","), ...lines].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "AE_Conversion_Report.csv";
  a.click();
  URL.revokeObjectURL(url);
}

const getTodayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const addDaysYmd = (ymd: string, days: number): string => {
  const [y, mo, da] = ymd.split("-").map((n) => parseInt(n, 10));
  const d = new Date(y, mo - 1, da);
  d.setDate(d.getDate() + days);
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

const DetailsTable: React.FC<{ title: string; rows: ConvertedStudentDetail[] }> = ({
  title,
  rows,
}) => (
  <section className="ae-conversion-modal__section">
    <h3>{title}</h3>
    <Table caption={title}>
      <thead>
        <tr>
          <th>Enrollment Date</th>
          <th>Student Id</th>
          <th>Name</th>
          <th>Mobile</th>
          <th>Email</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((s, i) => (
          <tr key={`${title}-${i}`}>
            <td>{formatEnrollmentDate(s.enrollmentDate)}</td>
            <td>{s.studentId ?? "—"}</td>
            <td>{s.studentName ?? "—"}</td>
            <td>{s.mobilenumber ?? "—"}</td>
            <td>{s.emailId ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  </section>
);

const AeConversionDetails: React.FC = () => {
  const initialFrom = useMemo(() => getTodayYmd(), []);
  const initialTo = useMemo(() => addDaysYmd(getTodayYmd(), 7), []);

  const [fromDate, setFromDate] = useState(initialFrom);
  const [toDate, setToDate] = useState(initialTo);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AeConversionEnrichedRow[]>([]);
  const [modalRow, setModalRow] = useState<AeConversionEnrichedRow | null>(null);

  const fetchRange = useCallback(async (from: string, to: string) => {
    if (!from || !to) {
      toast.error("Please select from and to dates.");
      return;
    }
    if (from > to) {
      toast.error("From date must be on or before to date.");
      return;
    }
    setLoading(true);
    setData([]);
    try {
      const raw = await getAeConversionReport(from, to);
      setData(enrichAeConversionRows(raw));
    } catch (e) {
      console.error(e);
      toast.error("Could not load AE conversion report.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void fetchRange(initialFrom, initialTo);
    }, 200);
    return () => window.clearTimeout(t);
  }, [initialFrom, initialTo, fetchRange]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void fetchRange(fromDate, toDate);
  };

  const sortedRows = useMemo(
    () =>
      [...data].sort((a, b) => {
        const byCategory = String(a.CategoryName ?? "").localeCompare(String(b.CategoryName ?? ""));
        if (byCategory !== 0) return byCategory;
        return String(a.TeacherName ?? "").localeCompare(String(b.TeacherName ?? ""));
      }),
    [data]
  );

  return (
    <Page>
      <PageHeader eyebrow="AE Reports" title="AE Conversion" />

      <Card>
        <form onSubmit={onSubmit}>
          <Toolbar>
            <Field label="From date" required>
              {(ctl) => (
                <TextInput
                  {...ctl}
                  type="date"
                  required
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={loading}
                />
              )}
            </Field>
            <Field label="To date" required>
              {(ctl) => (
                <TextInput
                  {...ctl}
                  type="date"
                  required
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={loading}
                />
              )}
            </Field>
            <div className="ae-reports__actions">
              <Button variant="primary" type="submit" loading={loading}>
                Submit
              </Button>
              <Button
                variant="secondary"
                disabled={loading || data.length === 0}
                onClick={() => exportConversionCsv(data)}
              >
                Export to CSV
              </Button>
            </div>
          </Toolbar>
        </form>
      </Card>

      {loading ? (
        <Loader label="Loading AE conversion report…" />
      ) : data.length === 0 ? (
        <EmptyState title="No data found for this range." description="Try a wider date range, then select Submit." />
      ) : (
        <Card flush className="ae-reports__scroll ae-reports__scroll--72">
          <Table caption="AE conversion by teacher and category" className="ae-reports__grid ae-conversion">
            <thead>
              <tr>
                <th rowSpan={2}>Teacher ID</th>
                <th rowSpan={2}>Teacher Name</th>
                <th rowSpan={2}>Category Name</th>
                <th rowSpan={2}>Total Slots</th>
                <th colSpan={3}>Classes Conducted</th>
                <th colSpan={3}>Classes Not Conducted</th>
                <th colSpan={3}>Classes Not Marked</th>
                <th colSpan={3}>Classes Conducted Percentage</th>
                <th colSpan={3}>Converted Students</th>
                <th rowSpan={2}>Conversion %</th>
                <th rowSpan={2}>Action</th>
              </tr>
              <tr>
                <th>Domestic</th>
                <th>International</th>
                <th>Total</th>
                <th>Domestic</th>
                <th>International</th>
                <th>Total</th>
                <th>Domestic</th>
                <th>International</th>
                <th>Total</th>
                <th>Domestic</th>
                <th>International</th>
                <th>Total</th>
                <th>Domestic</th>
                <th>International</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, idx) => {
                const totalConductedPct = conductedPctFromTotals(
                  row.totalConducted,
                  row.totalNotConducted,
                  row.totalNotMarked
                );
                return (
                  <tr key={`${row.TeacherId}-${row.CategoryName}-${idx}`}>
                    <td>{row.TeacherId ?? "—"}</td>
                    <td>{row.TeacherName ?? "—"}</td>
                    <td>{row.CategoryName ?? "—"}</td>
                    <td className="ds-num">{toNum(row.TotalSlots)}</td>
                    <td className="ds-num">{row.domestic.classConducted}</td>
                    <td className="ds-num">{row.international.classConducted}</td>
                    <td className="ds-num ds-strong">{row.totalConducted}</td>
                    <td className="ds-num">{row.domestic.classesNotConducted}</td>
                    <td className="ds-num">{row.international.classesNotConducted}</td>
                    <td className="ds-num ds-strong">{row.totalNotConducted}</td>
                    <td className="ds-num">{row.domestic.classesNotMarked}</td>
                    <td className="ds-num">{row.international.classesNotMarked}</td>
                    <td className="ds-num ds-strong">{row.totalNotMarked}</td>
                    <td className="ds-num">{row.domestic.conductedPctStr}</td>
                    <td className="ds-num">{row.international.conductedPctStr}</td>
                    <td className="ds-num ds-strong">{totalConductedPct}</td>
                    <td className="ds-num">{row.domestic.convertedStudents}</td>
                    <td className="ds-num">{row.international.convertedStudents}</td>
                    <td className="ds-num ds-strong">{row.totalConverted}</td>
                    <td className="ds-num ds-strong">{row.conversionPctStr}%</td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!rowHasConversionDetails(row)}
                        onClick={() => setModalRow(row)}
                      >
                        View details
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      <Modal isOpen={!!modalRow} toggle={() => setModalRow(null)} size="lg" scrollable centered className="ds-dialog">
        <h2 className="ds-dialog__title">Conversion Details</h2>
        <div className="ds-dialog__body ae-conversion-modal__body">
          {modalRow && (
            <>
              <p className="ds-text-muted">
                {modalRow.TeacherName} · {modalRow.CategoryName}
              </p>
              {modalRow.domestic.convertedStudentsDetails.length > 0 && (
                <DetailsTable
                  title="Domestic — converted students"
                  rows={modalRow.domestic.convertedStudentsDetails}
                />
              )}
              {modalRow.international.convertedStudentsDetails.length > 0 && (
                <DetailsTable
                  title="International — converted students"
                  rows={modalRow.international.convertedStudentsDetails}
                />
              )}
              {modalRow.domestic.convertedStudentsDetails.length === 0 &&
                modalRow.international.convertedStudentsDetails.length === 0 && (
                  <EmptyState title="No line items returned for this row." />
                )}
            </>
          )}
        </div>
        <div className="ds-dialog__actions">
          <Button variant="secondary" onClick={() => setModalRow(null)}>
            Close
          </Button>
        </div>
      </Modal>
    </Page>
  );
};

export default AeConversionDetails;
